import {clamp} from '@hm/fundamental'
import {useEffect, useMemo, useCallback} from 'react'

import {ViewBoxRect} from '../types'

const MIN_ZOOM = 0.5
const MAX_ZOOM = 10
const ZOOM_STEP_FACTOR = 1.1
const ZOOM_THRESHOLD = 0.0001
const WHEEL_ZOOM_SENSITIVITY = 0.002

export const useDrawingZoom = ({
  svg,
  baseViewBoxRect,
  viewBoxRect,
  setViewBoxRect
}: {
  svg: SVGSVGElement | null
  baseViewBoxRect: ViewBoxRect
  viewBoxRect: ViewBoxRect
  setViewBoxRect: React.Dispatch<React.SetStateAction<ViewBoxRect>>
}) => {
  const currentZoom = useMemo(
    () => baseViewBoxRect.width / viewBoxRect.width,
    [baseViewBoxRect, viewBoxRect.width]
  )

  const zoomPercentage = useMemo(() => Math.round(currentZoom * 100), [currentZoom])

  const isAtDefaultView = useMemo(() => {
    return (
      Math.abs(viewBoxRect.x - baseViewBoxRect.x) < ZOOM_THRESHOLD &&
      Math.abs(viewBoxRect.y - baseViewBoxRect.y) < ZOOM_THRESHOLD &&
      Math.abs(viewBoxRect.width - baseViewBoxRect.width) < ZOOM_THRESHOLD &&
      Math.abs(viewBoxRect.height - baseViewBoxRect.height) < ZOOM_THRESHOLD
    )
  }, [baseViewBoxRect, viewBoxRect])

  const canZoomIn = currentZoom < MAX_ZOOM - ZOOM_THRESHOLD
  const canZoomOut = currentZoom > MIN_ZOOM + ZOOM_THRESHOLD

  const applyZoom = useCallback(
    (requestedZoom: number) => {
      const minWidth = baseViewBoxRect.width / MAX_ZOOM
      const maxWidth = baseViewBoxRect.width / MIN_ZOOM
      const minHeight = baseViewBoxRect.height / MAX_ZOOM
      const maxHeight = baseViewBoxRect.height / MIN_ZOOM

      setViewBoxRect((prevViewBoxRect) => {
        const currentZoom = baseViewBoxRect.width / prevViewBoxRect.width
        const nextZoom = clamp(requestedZoom, MIN_ZOOM, MAX_ZOOM)

        if (Math.abs(nextZoom - currentZoom) < ZOOM_THRESHOLD) {
          return prevViewBoxRect
        }

        const scale = currentZoom / nextZoom
        const unclampedWidth = prevViewBoxRect.width * scale
        const unclampedHeight = prevViewBoxRect.height * scale
        const nextWidth = clamp(unclampedWidth, minWidth, maxWidth)
        const nextHeight = clamp(unclampedHeight, minHeight, maxHeight)

        const safeAnchor = 0.5

        const anchorX = prevViewBoxRect.x + safeAnchor * prevViewBoxRect.width
        const anchorY = prevViewBoxRect.y + safeAnchor * prevViewBoxRect.height

        const scaleX = nextWidth / prevViewBoxRect.width
        const scaleY = nextHeight / prevViewBoxRect.height

        const nextX = anchorX - (anchorX - prevViewBoxRect.x) * scaleX
        const nextY = anchorY - (anchorY - prevViewBoxRect.y) * scaleY

        return {x: nextX, y: nextY, width: nextWidth, height: nextHeight}
      })
    },
    [baseViewBoxRect, setViewBoxRect]
  )

  const handleZoomIn = useCallback(() => {
    applyZoom(currentZoom * ZOOM_STEP_FACTOR)
  }, [applyZoom, currentZoom])

  const handleZoomOut = useCallback(() => {
    applyZoom(currentZoom / ZOOM_STEP_FACTOR)
  }, [applyZoom, currentZoom])

  const handleZoomReset = useCallback(() => {
    setViewBoxRect(baseViewBoxRect)
  }, [baseViewBoxRect, setViewBoxRect])

  const handleWheelZoom = useCallback(
    (e: WheelEvent) => {
      e.preventDefault()

      if (!svg) return

      const bounds = svg.getBoundingClientRect()
      if (bounds.width <= 0 || bounds.height <= 0) return

      const offsetX = e.clientX - bounds.left
      const offsetY = e.clientY - bounds.top
      const zoomDelta = Math.exp(-e.deltaY * WHEEL_ZOOM_SENSITIVITY)
      const minWidth = baseViewBoxRect.width / MAX_ZOOM
      const maxWidth = baseViewBoxRect.width / MIN_ZOOM
      const minHeight = baseViewBoxRect.height / MAX_ZOOM
      const maxHeight = baseViewBoxRect.height / MIN_ZOOM

      setViewBoxRect((prevViewBoxRect) => {
        const currentZoom = baseViewBoxRect.width / prevViewBoxRect.width
        const nextZoom = clamp(currentZoom * zoomDelta, MIN_ZOOM, MAX_ZOOM)

        if (Math.abs(nextZoom - currentZoom) < ZOOM_THRESHOLD) {
          return prevViewBoxRect
        }

        const anchorRatio = {x: offsetX / bounds.width, y: offsetY / bounds.height}

        const scale = currentZoom / nextZoom
        const unclampedWidth = prevViewBoxRect.width * scale
        const unclampedHeight = prevViewBoxRect.height * scale
        const nextWidth = clamp(unclampedWidth, minWidth, maxWidth)
        const nextHeight = clamp(unclampedHeight, minHeight, maxHeight)

        const safeAnchorX = clamp(anchorRatio.x, 0, 1)
        const safeAnchorY = clamp(anchorRatio.y, 0, 1)

        const anchorX = prevViewBoxRect.x + safeAnchorX * prevViewBoxRect.width
        const anchorY = prevViewBoxRect.y + safeAnchorY * prevViewBoxRect.height

        const scaleX = nextWidth / prevViewBoxRect.width
        const scaleY = nextHeight / prevViewBoxRect.height

        const nextX = anchorX - (anchorX - prevViewBoxRect.x) * scaleX
        const nextY = anchorY - (anchorY - prevViewBoxRect.y) * scaleY

        return {x: nextX, y: nextY, width: nextWidth, height: nextHeight}
      })
    },
    [baseViewBoxRect, setViewBoxRect, svg]
  )

  useEffect(() => {
    if (!svg) return

    svg.addEventListener('wheel', handleWheelZoom, {passive: false})

    return () => {
      svg.removeEventListener('wheel', handleWheelZoom)
    }
  }, [handleWheelZoom, svg])

  return {
    viewBoxRect,
    setViewBoxRect,
    zoomPercentage,
    isAtDefaultView,
    canZoomIn,
    canZoomOut,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset
  }
}
